import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { InlineLoadingState } from '../LoadingSpinner';
import { AppMultiSelectDropdown } from '../AppMultiSelectDropdown';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { DatePicker } from '../DatePicker';
import {
  APP_BTN_OUTLINE_CLASS,
  APP_BTN_PRIMARY_CLASS,
  APP_INPUT_CLASS,
  APP_SELECT_TRIGGER_CLASS,
  APP_TEXTAREA_CLASS,
} from '../../ui/controlStyles';
import { SCHEDULING_STATUS_LABELS, type SchedulingStatus } from '../plantafel/plantafelTypes';
import { addDaysYMD } from '../../utils/date';
import { isoToLocalDateAndTime, localWallTimeToIso, shiftEndPreservingDuration, formatWallClockYmd, DEFAULT_ACTIVITY_TIMEZONE } from '../../utils/activityDateTime';
import { useCompanyTimezoneState } from '../../hooks/useCompanyTimezone';
import { ActivityIcon, FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import { parseEmployeesListResponse } from '../../utils/parseEmployeesApi';
import { UI_Z_CLASS, UI_Z_INDEX } from '../../utils/uiZIndex';
import { useDiscardGuard } from '../../hooks/useDiscardGuard';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { useAssigneeConflictConfirm } from './useAssigneeConflictConfirm';
import { availabilityBadge, unavailableAssignees, useAssigneeAvailability } from './useAssigneeAvailability';
import {
  ActivityDayCalendar,
  ActivityFormWithSideCalendar,
  filterActivitiesForEinsatzteam,
  type ActivityCalendarEntry,
} from './activityDayCalendar';
import { useChecklistsForSelect } from '../../hooks/useChecklistsForSelect';
import { buildChecklistSelectOptions } from '../../utils/checklistSelectOptions';
import {
  getDefaultChecklistIdForActivityType,
  nextChecklistIdForActivityTypeChange,
} from '../../utils/activityTypeDefaultChecklist';
import { leadSelectDropdownFields, leadSelectSiteFieldsFromApi } from '../../utils/leadSelectOption';
import {
  DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES,
  normalizePlantafelDefaultActivityDurationMinutes,
} from '../../utils/plantafelSettings';
import {
  ActivityRecurrenceFields,
  defaultRecurrenceForm,
  recurrencePayloadFromForm,
} from './ActivityRecurrenceFields';
import type { RecurrenceFormState } from '../../utils/activityRecurrence';

type LeadOption = {
  id: string;
  display_id: string;
  name: string;
  lead_title?: string;
  created_at?: string;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
  site_address?: string | null;
};
type ActivityTypeOption = { id: string; label: string; icon: string; default_checklist_id?: string | null };
type InstanceMemberOption = { user_id: string; email: string; display_name: string | null };
type EmployeeRow = { id: string; first_name: string; last_name: string };

type ActivityForm = {
  leadId: string;
  title: string;
  activity_type: string;
  checklist_id: string | null;
  scheduling_status: SchedulingStatus;
  dueDate: string;
  dueTimeStart: string;
  dueTimeEnd: string;
  notes: string;
  assignee_user_ids: string[];
  assignee_employee_ids: string[];
  recurrence: RecurrenceFormState;
};

const DEFAULT_ACTIVITY_TYPE_ID = FALLBACK_ACTIVITY_TYPES[0]?.id ?? 'task';
const MODAL_DROPDOWN_Z = UI_Z_INDEX.portalDropdownInModal;

const SCHEDULING_STATUS_OPTIONS = (
  Object.entries(SCHEDULING_STATUS_LABELS) as [SchedulingStatus, string][]
).map(([value, label]) => ({ value, label }));

function ActivityRequiredMark() {
  return <span className="text-red-500"> *</span>;
}

function companyTodayYmd(timeZone: string): string {
  return formatWallClockYmd(new Date(), timeZone);
}

function defaultTimeRange(
  timeZone: string,
  durationMinutes: number = DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES,
): { start: string; end: string } {
  const { time } = isoToLocalDateAndTime(new Date().toISOString(), timeZone);
  const [h, m] = time.split(':').map((x) => Number.parseInt(x, 10));
  const startMin = Math.min(
    23 * 60 + 30,
    Math.ceil(((Number.isFinite(h) ? h : 9) * 60 + (Number.isFinite(m) ? m : 0)) / 30) * 30,
  );
  const endMin = Math.min(24 * 60 - 1, startMin + Math.max(1, durationMinutes));
  const fmt = (mins: number) =>
    `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  return { start: fmt(startMin), end: fmt(endMin) };
}

type ActivityFormDefaults = {
  defaultDueDate?: string;
  defaultLeadId?: string;
  defaultTitle?: string;
  defaultNotes?: string;
  defaultDurationMinutes?: number;
};

function buildInitialForm(defaults?: ActivityFormDefaults, timeZone?: string): ActivityForm {
  const tz = timeZone ?? DEFAULT_ACTIVITY_TIMEZONE;
  const { start, end } = defaultTimeRange(
    tz,
    defaults?.defaultDurationMinutes ?? DEFAULT_PLANTAFEL_ACTIVITY_DURATION_MINUTES,
  );
  return {
    leadId: defaults?.defaultLeadId ?? '',
    title: defaults?.defaultTitle ?? '',
    activity_type: DEFAULT_ACTIVITY_TYPE_ID,
    checklist_id: null,
    scheduling_status: 'geplant',
    dueDate: defaults?.defaultDueDate ?? companyTodayYmd(tz),
    dueTimeStart: start,
    dueTimeEnd: end,
    notes: defaults?.defaultNotes ?? '',
    assignee_user_ids: [],
    assignee_employee_ids: [],
    recurrence: defaultRecurrenceForm(defaults?.defaultDueDate ?? companyTodayYmd(tz)),
  };
}

/**
 * Eingaben, die wirklich vom Nutzer kommen. Datum, Uhrzeit, Typ und Checkliste setzt das
 * Formular nach dem Laden selbst nach (Zeitzone, Standarddauer, erster Typ) — die zählen nicht.
 */
function userInputSnapshot(form: ActivityForm): string {
  return JSON.stringify([
    form.leadId,
    form.title.trim(),
    form.notes.trim(),
    form.scheduling_status,
    form.assignee_user_ids,
    form.assignee_employee_ids,
    form.recurrence.freq,
  ]);
}

type CreateActivityModalProps = {
  onClose: () => void;
  onCreated?: (activity: { id: string; lead_id?: string | number | null }) => void | Promise<void>;
  defaultDueDate?: string;
  defaultLeadId?: string;
  defaultTitle?: string;
  defaultNotes?: string;
};

export function CreateActivityModal({
  onClose,
  onCreated,
  defaultDueDate,
  defaultLeadId,
  defaultTitle,
  defaultNotes,
}: CreateActivityModalProps) {
  const { timeZone, ready: timeZoneReady } = useCompanyTimezoneState();
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  // Projektliste nur mit Projekt-Leserecht; das Einsatzteam kommt aus dem geteilten Mitarbeiter-Picker
  // (Mitarbeiter, Fuhrpark oder Projekte bearbeiten), die Standarddauer aus der Plantafel.
  const canViewProjects = can('web.projekte.view');
  const canPickEmployees = can('web.hr.view') || can('web.fuhrpark.view') || can('web.projekte.edit');
  const plantafelSettingsAvailable = modulesLoaded && isEnabled('plantafel');
  const seededTzRef = React.useRef(timeZone);
  const [form, setForm] = React.useState<ActivityForm>(() =>
    buildInitialForm({ defaultDueDate, defaultLeadId, defaultTitle, defaultNotes }, timeZone),
  );
  const [leads, setLeads] = React.useState<LeadOption[]>([]);
  const [activityTypes, setActivityTypes] = React.useState<ActivityTypeOption[]>([]);
  const [instanceMembers, setInstanceMembers] = React.useState<InstanceMemberOption[]>([]);
  const [employees, setEmployees] = React.useState<EmployeeRow[]>([]);
  const [loadingOptions, setLoadingOptions] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const conflictConfirm = useAssigneeConflictConfirm();
  const [error, setError] = React.useState<string | null>(null);
  const [scheduledActivities, setScheduledActivities] = React.useState<ActivityCalendarEntry[]>([]);
  const [defaultDurationApplied, setDefaultDurationApplied] = React.useState(false);
  const initialInputRef = React.useRef(userInputSnapshot(form));
  /** Klick daneben, ✕: mit Eingaben erst nachfragen. „Abbrechen“ schließt bewusst sofort. */
  const discardGuard = useDiscardGuard({
    dirty: userInputSnapshot(form) !== initialInputRef.current,
    onClose,
    disabled: saving,
    description: 'Die Aktivität ist noch nicht angelegt. Deine Eingaben gehen verloren.',
  });

  const checklists = useChecklistsForSelect();
  const checklistOptions = React.useMemo(
    () => buildChecklistSelectOptions(checklists, form.activity_type),
    [checklists, form.activity_type],
  );

  // Re-seed date/time defaults once company TZ is known (or changes), if user has not edited them.
  React.useEffect(() => {
    if (!timeZoneReady) return;
    if (seededTzRef.current === timeZone) return;
    setForm((f) => {
      const oldSeed = buildInitialForm(
        { defaultDueDate, defaultLeadId, defaultTitle, defaultNotes },
        seededTzRef.current,
      );
      const untouched =
        f.dueDate === oldSeed.dueDate &&
        f.dueTimeStart === oldSeed.dueTimeStart &&
        f.dueTimeEnd === oldSeed.dueTimeEnd;
      if (!untouched) return f;
      const next = buildInitialForm(
        { defaultDueDate, defaultLeadId, defaultTitle, defaultNotes },
        timeZone,
      );
      return {
        ...f,
        dueDate: next.dueDate,
        dueTimeStart: next.dueTimeStart,
        dueTimeEnd: next.dueTimeEnd,
      };
    });
    seededTzRef.current = timeZone;
  }, [timeZone, timeZoneReady, defaultDueDate, defaultLeadId, defaultTitle, defaultNotes]);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/activities/scheduled', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setScheduledActivities(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setScheduledActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (defaultDurationApplied || !plantafelSettingsAvailable) return;
    let cancelled = false;
    fetch('/api/settings/plantafel', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const duration = normalizePlantafelDefaultActivityDurationMinutes(
          (data as { default_activity_duration_minutes?: unknown } | null)
            ?.default_activity_duration_minutes,
        );
        setForm((f) => {
          const [h, m] = f.dueTimeStart.split(':').map((x) => Number.parseInt(x, 10));
          const startMin = (Number.isFinite(h) ? h : 9) * 60 + (Number.isFinite(m) ? m : 0);
          const endMin = Math.min(24 * 60 - 1, startMin + duration);
          const fmt = (mins: number) =>
            `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
          return { ...f, dueTimeEnd: fmt(endMin) };
        });
        setDefaultDurationApplied(true);
      })
      .catch(() => {
        if (!cancelled) setDefaultDurationApplied(true);
      });
    return () => {
      cancelled = true;
    };
  }, [defaultDurationApplied, plantafelSettingsAvailable]);

  React.useEffect(() => {
    if (!capabilitiesLoaded) return;
    let cancelled = false;
    setLoadingOptions(true);
    void Promise.all([
      canViewProjects
        ? fetch('/api/leads', { credentials: 'include' }).then((res) => (res.ok ? res.json() : []))
        : Promise.resolve([]),
      fetch('/api/settings/activity-types', { credentials: 'include' }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/me/members', { credentials: 'include' }).then((res) =>
        res.ok ? res.json() : { members: [] },
      ),
      canPickEmployees
        ? fetch('/api/employees/picker', { credentials: 'include' }).then((res) => (res.ok ? res.json() : null))
        : Promise.resolve(null),
    ])
      .then(([leadsData, typesData, membersData, employeesData]) => {
        if (cancelled) return;
        const leadOptions = Array.isArray(leadsData)
          ? leadsData
              .map((l: {
                id?: unknown;
                display_id?: unknown;
                name?: unknown;
                lead_title?: unknown;
                created_at?: unknown;
                site_address?: unknown;
                baustelle?: {
                  bestellnummer?: unknown;
                  address?: unknown;
                  contact?: { name?: unknown; first_name?: unknown; last_name?: unknown } | null;
                } | null;
              }) => {
                const site = leadSelectSiteFieldsFromApi(l);
                return {
                  id: String(l.id ?? ''),
                  display_id: String(l.display_id ?? ''),
                  name: String(l.name ?? ''),
                  lead_title: String(l.lead_title ?? ''),
                  created_at: String(l.created_at ?? ''),
                  bestellnummer: site.bestellnummer,
                  site_contact_name: site.site_contact_name,
                  site_address: site.site_address,
                };
              })
              .filter((l) => l.id.length > 0)
          : [];
        const typeOptions = Array.isArray(typesData)
          ? typesData.map((t: { id?: unknown; label?: unknown; icon?: unknown; default_checklist_id?: unknown }) => ({
              id: String(t.id ?? ''),
              label: String(t.label ?? t.id ?? ''),
              icon: String(t.icon ?? 'task'),
              default_checklist_id: t.default_checklist_id ? String(t.default_checklist_id) : null,
            }))
          : [];
        const memberOptions = Array.isArray(membersData?.members)
          ? membersData.members
              .map((m: { user_id?: unknown; email?: unknown; display_name?: string | null }) => ({
                user_id: String(m.user_id ?? ''),
                email: String(m.email ?? ''),
                display_name: m.display_name ?? null,
              }))
              .filter((m: InstanceMemberOption) => m.user_id.length > 0)
          : [];
        const employeeRows = Array.isArray((employeesData as { items?: unknown })?.items)
          ? ((employeesData as { items: unknown[] }).items ?? [])
          : (parseEmployeesListResponse(employeesData) as unknown[]);
        const employeeOptions = employeeRows
          .map((e) => {
            const row = e as { id?: unknown; first_name?: unknown; last_name?: unknown };
            return {
              id: String(row.id ?? ''),
              first_name: String(row.first_name ?? ''),
              last_name: String(row.last_name ?? ''),
            };
          })
          .filter((e) => e.id.length > 0);

        setLeads(leadOptions);
        setActivityTypes(typeOptions);
        setInstanceMembers(memberOptions);
        setEmployees(employeeOptions);
        setForm((prev) => {
          const nextType = typeOptions[0]?.id ?? prev.activity_type;
          return {
            ...prev,
            activity_type: nextType,
            checklist_id:
              prev.checklist_id ??
              getDefaultChecklistIdForActivityType(typeOptions, nextType),
          };
        });
      })
      .catch(() => {
        if (!cancelled) setError('Formular konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [capabilitiesLoaded, canViewProjects, canPickEmployees]);

  const handleSave = async (confirmConflicts = false) => {
    if (!form.title.trim() || !form.dueDate) return;
    setSaving(true);
    setError(null);
    try {
      const dueAt = localWallTimeToIso(form.dueDate, form.dueTimeStart || '09:00', timeZone);
      const endAt = localWallTimeToIso(form.dueDate, form.dueTimeEnd || '10:00', timeZone);
      const recurrence = recurrencePayloadFromForm(form.recurrence, form.dueDate);
      const res = await fetch('/api/activities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lead_id: form.leadId || null,
          title: form.title.trim(),
          activity_type: form.activity_type || DEFAULT_ACTIVITY_TYPE_ID,
          checklist_id: form.checklist_id || null,
          scheduling_status: form.scheduling_status,
          due_at: dueAt,
          end_at: endAt,
          notes: form.notes.trim() || undefined,
          assignee_user_ids: form.assignee_user_ids,
          assignee_employee_ids: form.assignee_employee_ids,
          ...(recurrence ? { recurrence } : {}),
          ...(confirmConflicts ? { confirm_conflicts: true } : {}),
        }),
      });
      if (await conflictConfirm.intercept(res, () => handleSave(true))) return;
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Aktivität konnte nicht erstellt werden.');
      }
      const created = (await res.json()) as { id: string; lead_id?: string | number | null };
      await onCreated?.(created);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktivität konnte nicht erstellt werden.');
    } finally {
      setSaving(false);
    }
  };

  const typeOptions =
    activityTypes.length > 0 ? activityTypes : FALLBACK_ACTIVITY_TYPES.map((t) => ({ id: t.id, label: t.label, icon: t.icon }));

  const activityTypesList = typeOptions;

  const activityCalendarActivities = React.useMemo(
    () => filterActivitiesForEinsatzteam(scheduledActivities, form.assignee_employee_ids),
    [scheduledActivities, form.assignee_employee_ids],
  );

  const availability = useAssigneeAvailability({
    dueAt: form.dueDate && form.dueTimeStart ? localWallTimeToIso(form.dueDate, form.dueTimeStart, timeZone) : null,
    endAt: form.dueDate && form.dueTimeEnd ? localWallTimeToIso(form.dueDate, form.dueTimeEnd, timeZone) : null,
    enabled: timeZoneReady,
  });
  const unavailable = React.useMemo(
    () =>
      unavailableAssignees(availability, form.assignee_employee_ids, (id) => {
        const e = employees.find((row) => row.id === id);
        return e ? `${e.first_name} ${e.last_name}`.trim() : 'Mitarbeiter';
      }),
    [availability, form.assignee_employee_ids, employees],
  );

  const shiftActivityFormDay = React.useCallback((delta: number) => {
    setForm((f) => ({
      ...f,
      dueDate: addDaysYMD(f.dueDate || companyTodayYmd(timeZone), delta),
    }));
  }, [timeZone]);

  const activityDayCalendarEl = (
    <ActivityDayCalendar
      displayDate={form.dueDate || companyTodayYmd(timeZone)}
      onPrevDay={() => shiftActivityFormDay(-1)}
      onNextDay={() => shiftActivityFormDay(1)}
      activities={activityCalendarActivities}
      activityTypes={activityTypesList}
      timeZone={timeZone}
      unavailable={unavailable}
      previewSlot={{
        title:
          form.title.trim() ||
          activityTypesList.find((t) => t.id === form.activity_type)?.label ||
          'Neue Aktivität',
        startTime: form.dueTimeStart,
        endTime: form.dueTimeEnd,
        activityType: form.activity_type,
      }}
    />
  );

  const formFieldsTop = (
    <>
      {canViewProjects ? (
        <div>
          <label className="mb-1 block text-xs font-medium text-app-text-muted">
            Projekt / Anfrage (optional)
          </label>
          <AppSelectDropdown
            className="w-full"
            value={form.leadId}
            onChange={(value) => setForm((f) => ({ ...f, leadId: value }))}
            placeholder="Bitte wählen …"
            searchable
            triggerClassName={APP_SELECT_TRIGGER_CLASS}
            menuZIndex={MODAL_DROPDOWN_Z}
            options={[
              { value: '', label: '— Kein Projekt —' },
              ...leads.map((lead) => ({
                value: lead.id,
                ...leadSelectDropdownFields(lead),
              })),
            ]}
          />
        </div>
      ) : null}
      <div>
        <label className="mb-1 block text-xs font-medium text-app-text-muted">
          Titel
          <ActivityRequiredMark />
        </label>
        <input
          type="text"
          className={APP_INPUT_CLASS}
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="z. B. Anruf mit Kunde"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="min-w-0">
          <label className="mb-1 block text-xs font-medium text-app-text-muted">Typ</label>
          <AppSelectDropdown
            className="w-full"
            value={form.activity_type}
            onChange={(value) =>
              setForm((f) => ({
                ...f,
                activity_type: value,
                checklist_id: nextChecklistIdForActivityTypeChange(
                  activityTypesList,
                  f.activity_type,
                  value,
                  f.checklist_id,
                ),
              }))
            }
            triggerClassName={APP_SELECT_TRIGGER_CLASS}
            menuZIndex={MODAL_DROPDOWN_Z}
            options={typeOptions.map((type) => ({
              value: type.id,
              label: type.label,
              icon: <ActivityIcon iconId={type.icon || type.id} className="h-4 w-4 text-pipedrive-blue" />,
            }))}
          />
        </div>
        <div className="min-w-0">
          <label className="mb-1 block text-xs font-medium text-app-text-muted">Status</label>
          <AppSelectDropdown
            className="w-full"
            value={form.scheduling_status}
            onChange={(value) => setForm((f) => ({ ...f, scheduling_status: value as SchedulingStatus }))}
            triggerClassName={APP_SELECT_TRIGGER_CLASS}
            menuZIndex={MODAL_DROPDOWN_Z}
            options={SCHEDULING_STATUS_OPTIONS}
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-app-text-muted">
          Checkliste
        </label>
        <AppSelectDropdown
          className="w-full"
          value={form.checklist_id ?? ''}
          onChange={(value) => setForm((f) => ({ ...f, checklist_id: value || null }))}
          triggerClassName={APP_SELECT_TRIGGER_CLASS}
          menuZIndex={MODAL_DROPDOWN_Z}
          options={checklistOptions}
          placeholder="Keine Checkliste"
          searchable
          searchPlaceholder="Checkliste suchen…"
        />
      </div>
      <div className={`grid gap-2 ${form.recurrence.freq !== 'none' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
        <div>
          <label className="mb-1 block text-xs font-medium text-app-text-muted">Datum</label>
          <DatePicker
            value={form.dueDate || ''}
            onChange={(v) => setForm((f) => ({ ...f, dueDate: v }))}
            className="w-full"
            placeholder="TT.MM.JJJJ"
            menuZIndex={MODAL_DROPDOWN_Z}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-app-text-muted">Von</label>
          <input
            type="time"
            className={APP_INPUT_CLASS}
            value={form.dueTimeStart}
            onChange={(e) => {
              const nextStartTime = e.target.value;
              setForm((f) => {
                const shifted = shiftEndPreservingDuration({
                  startDate: f.dueDate,
                  endDate: f.dueDate,
                  startTime: f.dueTimeStart,
                  endTime: f.dueTimeEnd,
                  nextStartTime,
                  timeZone,
                });
                return {
                  ...f,
                  dueTimeStart: shifted.startTime,
                  dueTimeEnd: shifted.endTime,
                };
              });
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-app-text-muted">Bis</label>
          <input
            type="time"
            className={APP_INPUT_CLASS}
            value={form.dueTimeEnd}
            onChange={(e) => setForm((f) => ({ ...f, dueTimeEnd: e.target.value }))}
          />
        </div>
        {form.recurrence.freq !== 'none' ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-app-text-muted">Endet</label>
            <DatePicker
              value={form.recurrence.untilDate}
              onChange={(untilDate) =>
                setForm((f) => ({
                  ...f,
                  recurrence: { ...f.recurrence, untilDate, endMode: 'until' },
                }))
              }
              className="w-full"
              placeholder="TT.MM.JJJJ"
              menuZIndex={MODAL_DROPDOWN_Z}
            />
          </div>
        ) : null}
      </div>
      <ActivityRecurrenceFields
        form={form.recurrence}
        onChange={(recurrence) => setForm((f) => ({ ...f, recurrence }))}
        startYmd={form.dueDate}
        compact
        menuZIndex={MODAL_DROPDOWN_Z}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="min-w-0">
          <label className="mb-1 block text-xs font-medium text-app-text-muted">
            Projektleiter
          </label>
          <AppMultiSelectDropdown
            label="Projektleiter"
            selected={form.assignee_user_ids}
            onChange={(v) => setForm((f) => ({ ...f, assignee_user_ids: v }))}
            options={instanceMembers.map((m) => ({
              value: m.user_id,
              label: (m.display_name && m.display_name.trim()) || m.email,
            }))}
            variant="form"
            menuZIndex={MODAL_DROPDOWN_Z}
          />
        </div>
        <div className="min-w-0">
          <label className="mb-1 block text-xs font-medium text-app-text-muted">
            Einsatzteam
          </label>
          <AppMultiSelectDropdown
            label="Einsatzteam"
            selected={form.assignee_employee_ids}
            onChange={(v) => setForm((f) => ({ ...f, assignee_employee_ids: v }))}
            options={employees.map((e) => ({
              value: e.id,
              label: `${e.first_name} ${e.last_name}`.trim(),
              unavailable: availabilityBadge(availability[e.id]),
            }))}
            variant="form"
            menuZIndex={MODAL_DROPDOWN_Z}
          />
        </div>
      </div>
    </>
  );

  const formColumnContent = loadingOptions ? (
    <InlineLoadingState label="Lade Formular…" className="py-8" />
  ) : (
    <div className="space-y-4">
      {formFieldsTop}
      <div>
        <label className="mb-1 block text-xs font-medium text-app-text-muted">Beschreibung</label>
        <textarea
          className={`${APP_TEXTAREA_CLASS} min-h-[5rem] resize-none`}
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        />
      </div>
    </div>
  );

  return createPortal(
    <div
      className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawerModal} flex items-center justify-center bg-black/50 p-4`}
      {...discardGuard.backdropProps}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-activity-modal-title"
        className="flex h-[min(92vh,860px)] max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <ActivityFormWithSideCalendar
          className="h-full min-h-0 flex-1 overflow-hidden"
          calendarFrom="sm"
          formColumn={
            <>
              <div className="flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-1">
                <h3 id="create-activity-modal-title" className="text-lg font-semibold text-app-text">
                  Aktivität planen
                </h3>
                <button
                  type="button"
                  onClick={discardGuard.requestClose}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text"
                  aria-label="Schließen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{formColumnContent}</div>
              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
                {error ? <p className="mr-auto text-sm text-red-600">{error}</p> : null}
                <button type="button" className={APP_BTN_OUTLINE_CLASS} onClick={onClose}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className={APP_BTN_PRIMARY_CLASS}
                  disabled={loadingOptions || saving || !form.title.trim() || !form.dueDate}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Speichert…' : 'Aktivität anlegen'}
                </button>
              </div>
            </>
          }
          calendar={activityDayCalendarEl}
        />
      </div>
      {conflictConfirm.dialog}
      {discardGuard.dialog}
    </div>,
    document.body,
  );
}
